import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import type { AgentRun } from "../models/run";

// Operator-supplied resolution of an OpenVariable. Mirrors
// inrange_agents.resolutions.Resolution on the backend.
export interface Resolution {
  project_id: string;
  variable_key: string;
  variable_name: string;
  variable_description: string;
  resolution_text: string;
  created_at: string;
  created_by: string | null;
  source_run_id: string | null;
}

export interface ResolveVariableInput {
  project_id: string;
  variable_name: string;
  variable_description: string;
  resolution_text: string;
  source_run_id?: string;
}

interface ResolveVariableResponse {
  resolution: Resolution;
  new_run: AgentRun;
  cross_project_lessons_used: number;
}

interface ResolutionsListResponse {
  resolutions: Resolution[];
}

const RESOLUTIONS_QUERY_KEY = "inrange-agents-resolutions";

// React Query hook for listing resolutions on one project. Used by
// the Run Detail page to render resolved-state badges on the
// open-variables cards.
export const useResolutionsList = (project_id?: string) => {
  const http = useAgentsHttp();
  return useQuery<Resolution[]>(
    [RESOLUTIONS_QUERY_KEY, project_id ?? null],
    async () => {
      if (!project_id) return [];
      const resp = await http<ResolutionsListResponse>("/open-variables/list",
        { project_id },
      );
      return resp.resolutions;
    },
    {
      enabled: Boolean(project_id),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );
};

// Save a resolution and trigger an incremental Atlas re-run. The
// returned response includes the new AgentRun (already persisted
// by the backend); we invalidate the runs-list query so the
// Operations page picks up the new row, plus the resolutions
// query so the Run Detail page re-renders with the resolved badge.
//
// Atlas does NOT re-read source documents on this path - it reasons
// over its prior decision plus the resolution. See backend module
// inrange_agents.rerun for the prompt + cost properties.
export const useResolveOpenVariable = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<ResolveVariableResponse, Error, ResolveVariableInput>(
    (input) =>
      http<ResolveVariableResponse>("/open-variables/resolve",
        input,
      ),
    {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["inrange-agents-runs-list"] });
        qc.invalidateQueries({ queryKey: ["inrange-agents-run-get"] });
        qc.invalidateQueries({ queryKey: [RESOLUTIONS_QUERY_KEY] });
      },
    },
  );
};
